import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { v4 as uuid } from 'uuid';
import {
  Button,
  Icon,
  Input,
  message,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Spin,
  Tag,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { validate as uuidValidate } from 'uuid';

import {
  BLSaveButton1,
  BusinessLogicContainer1,
  BusinessLogicSaveButton1,
  HorPopover,
  RuleEditingContainer1,
  TableEditContainer1,
} from '../../containers/ComplexDocumentDesign/ComplexPdfInserts/styles';
import { deepCopy } from '../../utils/javascript';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../context/complexDocumentContext';
import { EQUALS_OPERATORS, OPERATOR_KEY, OPERATOR_SYMBOLS, OPERATOR_VALUES, STARTS_OR_CONTAINS_OPERATOR } from '../../containers/ComplexDocumentDesign/BusinessLogicTab';
import { BUSINESS_LOGIC_TAB } from '../../containers/ComplexDocumentDesign/translationValues';
import {
  BusinessLogicFooter,
  DisableContainer,
  Placeholder,
  StyledSelect,
  TableLogicalOperator,
  TableOperator,
} from '../../containers/ComplexDocumentDesign/BusinessLogicTab/styles';
import { dummyCallback } from '../../utils/complexDocumentConstants';
import { AddIcon, BusinessLogicSvg, minusIcon } from '../../images/svgIcons';
import { PurpleTooltip } from '../../containers/ComplexDocumentDesign/BusinessLogicTab/businessLogicTable';
import { updateInsertBusinessLogic } from '../../utils/services/insertsApi';
import { INSERTS_TAB } from '../../containers/ComplexDocumentDesign/ComplexPdfInserts/translationValues';
import { ENTERPRISE_ENV, getBusinessLogicByType } from '../../utils/helper';
import { useSessionContext } from '../../context/sessionContext';
import { generateTextFromPrompt, saveGroupingDetails, updateEmailBusinessLogic } from '../../utils/services/businessLogicApi';
import SearchSelect from '../SearchSelect';
import { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';
import { BusinessLogic } from '../../context/businessLogicContext';
import { BlModalContainer } from './styles';
import { updateBusinessLogic } from '../../utils/services/businessLogicApi';

const Option = Select.Option;

const NAMESPACES = ['BUSINESS_LOGIC'];
const EXCLUDED_VARIABLE_TYPES = ['ListString', 'ListDate'];
export const getDefaultVariable = () => {
  return {
    variableId: uuid(),
    type: '',
    isNew: true,
  };
};

export const getDefaultExtraCondition = () => {
  return {
    id: uuid(),
    operator: OPERATOR_VALUES.EQUALS,
    value: '',
  };
};

export const getDefaultCondition = (variableId = '') => {
  return {
    id: uuid(),
    operator: OPERATOR_VALUES.EQUALS,
    value: '',
    variableId: variableId,
    isNew: true,
    extraConditions: [],
  };
};
export const getDefaultRule = defaultConditions => {
  return {
    conditions: defaultConditions || [getDefaultCondition()],
    isNew: true,
    ruleText: '',
    expression: '',
  };
};
export const getDefaultRule1 = defaultConditions => {
  return {
    id: uuid(),
    conditions: defaultConditions || [getDefaultCondition()],
    blockId: '',
    isNew: true,
    ruleText: '',
    expression: '',
    isStandalone: false,
  };
};

export const getDefaultVariable1 = () => {
  return {
    variableId: uuid(),
    type: '',
    isNew: true,
  };
};

const newBusinessLogic = (name, documentId) => {
  return {
    id: uuid(),
    variables: [getDefaultVariable()],
    ...getDefaultRule(),
  };
};

const variableColors = {
  blue: '#0000ff',
  blueviolet: '#8a2be2',
  brown: '#a52a2a',
  crimson: '#dc143c',
  darkblue: '#00008b',
  darkgreen: '#006400',
  darkmagenta: '#8b008b',
  darkorange: '#ff8c00',
  darkred: '#8b0000',
  darkviolet: '#9400d3',
};
const allKeys = Object.keys(variableColors);
const numOfColors = allKeys.length - 1;

export const BusinessLogicPopoverInfo = ({ insert, globalVariables }) => {
  const { businessLogic } = insert;
  const { conditions } = businessLogic;
  let colorIndex = 0;

  const getRandomColor = () => {
    if (colorIndex > numOfColors) {
      colorIndex = 0;
    }
    const randomColor = variableColors[allKeys[colorIndex]];
    colorIndex++;
    return randomColor;
  };

  const generateExpressions = (conditions, masterVariableId, prevConValid = false) => {
    let promptExpression = [];
    conditions.forEach((condition, index) => {
      const { extraConditions, variableId, operator, value } = condition;
      const isExtraCondition = !!(extraConditions && extraConditions.length);
      const varId = masterVariableId || variableId;
      if (value) {
        if (index !== 0 && !masterVariableId && prevConValid) {
          promptExpression.push(<span>{' and '}</span>);
        }
        if (isExtraCondition) {
          promptExpression.push(<span>{'('}</span>);
        }
        if (masterVariableId) {
          if (!prevConValid) {
            promptExpression.push(<span>{' and ('}</span>);
          } else {
            promptExpression.push(<span>{' or '}</span>);
          }
        }
        const variable = globalVariables?.find(variable => variable.id === varId);
        const variableName = variable?.placeHolderText;
        if (EQUALS_OPERATORS.includes(operator)) {
          promptExpression.push(
            <span style={{ color: getRandomColor() }}>{`${variableName}`}</span>,
          );
          promptExpression.push(<span>{` ${operator} ${value}`}</span>);
        } else if (operator === OPERATOR_VALUES.EQUALS_ANY_OF) {
          const result = value
            .split(',')
            .map(v => `${v}`)
            .join(',');
          promptExpression.push(
            <span style={{ color: getRandomColor() }}>{`${variableName}`}</span>,
          );
          promptExpression.push(<span>{` ${operator} [${result}]`}</span>);
        } else {
          promptExpression.push(
            <span style={{ color: getRandomColor() }}>{`${variableName}`}</span>,
          );
          promptExpression.push(<span>{` ${operator} ${value}`}</span>);
        }
        prevConValid = true;
      } else {
        prevConValid = false;
      }

      if (isExtraCondition) {
        const extPExp = generateExpressions(extraConditions, variableId, prevConValid);
        if (extPExp) {
          promptExpression = [...promptExpression, ...extPExp];
          promptExpression.push(<span>{`)`}</span>);
        }
      }
    });
    return promptExpression;
  };

  const promptExpression = generateExpressions(conditions);
  return <div>{promptExpression}</div>;
};


export const BusinessLogicOptionPopover = ({
  showBlModal,
  deleteBusinessLogic,
  insert,
  globalVariables,
  t,
  isInteractiveDesign
}) => {
  const { businessLogic, flowUUID } = insert;
  const [showPrimaryPopover, setShowPrimaryPopover] = useState(false);
  const [showSecondaryPopover, setShowSecondaryPopover] = useState(false);

  const verticalPopoverContent = (
    <div>
      {businessLogic?.ruleText ? (
        businessLogic.ruleText
      ) : flowUUID && businessLogic?.rules[0]?.ruleText ? (businessLogic.rules[0].ruleText) : (
        <BusinessLogicPopoverInfo insert={insert} globalVariables={globalVariables} />
      )}
    </div>
  );

  const onSuccess = () => {
    setShowSecondaryPopover(false);
  };

  const horizontalPopoverContent = (
    <HorPopover>
      {!isInteractiveDesign &&
        <>
          <Tooltip
            title={
              !!businessLogic
                ? t(
                  INSERTS_TAB.EDIT_BUSINESS_LOGIC_LABEL.key,
                  INSERTS_TAB.EDIT_BUSINESS_LOGIC_LABEL.value,
                )
                : t(
                  INSERTS_TAB.ADD_BUSINESS_LOGIC_LABEL.key,
                  INSERTS_TAB.ADD_BUSINESS_LOGIC_LABEL.value,
                )
            }
            placement="bottom"
          >
            <Button
              onClick={() => {
                showBlModal(true);
                setShowPrimaryPopover(false);
              }}
            >
              <Icon type={!!businessLogic ? 'edit' : 'plus'} />
            </Button>
          </Tooltip>
          {!!businessLogic ? (
            <Tooltip
              title={t(
                INSERTS_TAB.DELETE_BUSINESS_LOGIC_LABEL.key,
                INSERTS_TAB.DELETE_BUSINESS_LOGIC_LABEL.value,
              )}
              placement="bottom"
            >
              <Popconfirm
                trigger="click"
                placement={flowUUID ? 'left' : "top"}
                title={t(
                  INSERTS_TAB.DELETE_INSERT_BUSINESS_LOGIC_QUESTION_LABEL.key,
                  INSERTS_TAB.DELETE_INSERT_BUSINESS_LOGIC_QUESTION_LABEL.value,
                )}
                onConfirm={() => deleteBusinessLogic(onSuccess)}
                cancelText={t(INSERTS_TAB.NO_LABEL.key, INSERTS_TAB.NO_LABEL.value)}
                okText={t(INSERTS_TAB.YES_LABEL.key, INSERTS_TAB.YES_LABEL.value)}
              >
                <Button>
                  <Icon type="delete" />
                </Button>
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      }
      {!!businessLogic && (
        <>
          <Tooltip
            title={t(
              INSERTS_TAB.VIEW_BUSINESS_LOGIC_LABEL.key,
              INSERTS_TAB.VIEW_BUSINESS_LOGIC_LABEL.value,
            )}
            placement="bottom"
          >
            <Popover
              content={verticalPopoverContent}
              trigger="click"
              placement={flowUUID ? 'left' : "top"}
              overlayClassName="bl-vertical-popover"
              visible={showSecondaryPopover}
              onVisibleChange={vis => setShowSecondaryPopover(vis)}
            >
              <Button>
                <Icon type="eye" />
              </Button>
            </Popover>
          </Tooltip>
        </>
      )}
    </HorPopover>
  );

  return (
    <Tooltip
      title={t(INSERTS_TAB.BUSINESS_LOGIC_LABEL.key, INSERTS_TAB.BUSINESS_LOGIC_LABEL.value)}
      placement="bottom"
    >
      <Popover
        content={horizontalPopoverContent}
        trigger="click"
        overlayClassName="bl-hor-popover"
        visible={showPrimaryPopover}
        onVisibleChange={vis => {
          setShowPrimaryPopover(vis);
          if (!vis) {
            setShowSecondaryPopover(false);
          }
        }}
      >
        <Icon component={BusinessLogicSvg} />
      </Popover>
    </Tooltip>
  );
};

const TableDraggadbleHead = ({ item, index, t, ruleHintTooltipProps, ...props }) => {
  return <>{index === 0 ? <th>{item}</th> : <th id={`rule-${index}`}>{item}</th>}</>;
};

export const TableHead = ({ tableHeadData = [], flowHints, isNewFlow, t, ...props }) => {
  return (
    <thead>
      <tr>
        {tableHeadData.map((item, index) => (
          <TableDraggadbleHead item={item} index={index} t={t} />
        ))}
      </tr>
    </thead>
  );
};

const updateTableColumnHeight = () => {
  const trs = document.querySelectorAll('div.table-container > table > tbody > tr');
  trs &&
    trs.forEach(tr => {
      const { firstChild, clientHeight } = tr;
      firstChild.style.height = clientHeight + 'px';
    });
};

const TableTags = ({ rowItem, extraItem, updateOperatorByKey }) => {
  const item = extraItem || rowItem;
  const tags = item.value ? item.value.split(',') : [];
  const handleClose = tagIndex => {
    const newTags = tags.filter((item, index) => index !== tagIndex).join(',');
    updateOperatorByKey(newTags, rowItem, 'value', extraItem);
  };
  return tags.map((tag, index) => (
    <Tag closable onClose={() => handleClose(index)} key={tag + index}>
      {tag}
    </Tag>
  ));
};

export const TableMainBody = ({ tableBodyData = [], globalVariables, isNewFlow, t, ...props }) => {
  const {
    updateVariableWithKey,
    updateOperatorByKey,
    setRuleDeleteIndex,
    flowHints,
    updateFlowHints,
    addExtraCondition,
    deleteExtraCondition,
    deleteCondition,
    disabled
  } = props;

  const [variableHintProps, setVariableHintProps] = useState({});

  useEffect(() => updateTableColumnHeight(), [tableBodyData]);

  useEffect(() => {
    let showTimeout;
    if (isNewFlow) {
      showTimeout = setTimeout(() => setVariableHintProps({ visible: true }), 3500);
    }
    return () => {
      clearTimeout(showTimeout);
    };
  }, []);

  const variablePlaceholderClick = (value, variableItem, key) => {
    updateVariableWithKey(value, variableItem, key);
    const timeout = setTimeout(() => {
      const variableNode = document.getElementById(
        `${variableItem.variableId}-rule-condition-select`,
      );
      variableNode && variableNode.click();
      clearTimeout(timeout);
    }, 100);
  };

  const handleVariableSelect = (value, rowItem, bodyIndex) => {
    const { key } = value;
    updateVariableWithKey(key, rowItem, 'variableId');
    const varType = globalVariables.find(v => v.id === key)?.type?.toLowerCase();
    updateVariableWithKey(varType, rowItem, 'type');
    if (isNewFlow) {
      updateFlowHints({ ...flowHints, variableHintDone: true });
      updateOperatorByKey(false, tableBodyData[0][1], 'isNew');
      const hideTimeout = setTimeout(() => {
        setVariableHintProps({ visible: false });
        setVariableHintProps({});
        clearTimeout(hideTimeout);
      }, 1);
    }
    tableBodyData[bodyIndex].forEach((item, index) =>
      index !== 0 ? updateOperatorByKey(key, item, 'variableId') : null,
    );
  };

  const onMouseHoverRow = event => {
    const { clientHeight, lastChild } = event.currentTarget;
    // height of the parent tr divided by 2 and subtract 12 pixels
    // which is the radius of the subtract icon to position the element at center.
    lastChild.style.marginTop = clientHeight / 2 - 12 + 'px';
  };

  //handle enter or blur  for 'equals any of' input.
  const handleEnterOrBlur = (event, rowItem, extraItem) => {
    if ((event.which === 13 || event.type === 'blur') && event.target.value) {
      const item = extraItem || rowItem;
      const isTagAlreadyExists = item.value.split(',').includes(event.target.value);
      if (!isTagAlreadyExists) {
        const previousValue = item.value ? `${item.value},` : '';
        const value = previousValue + event.target.value;
        updateOperatorByKey(value, rowItem, 'value', extraItem);
        event.target.value = '';
      } else {
        message.error(
          t(BUSINESS_LOGIC_TAB.UNIQUE_VALUE_ERROR.key, BUSINESS_LOGIC_TAB.UNIQUE_VALUE_ERROR.value),
        );
      }
    }
  };

  return tableBodyData.map((bodyItem, bodyIndex) => {
    return (
      <tr onMouseEnter={onMouseHoverRow}>
        {bodyItem.map((rowItem, rowIndex) => {
          if (rowIndex === 0) {
            const { variableId, isNew } = rowItem;
            const name = globalVariables?.find(gVar => gVar.id === variableId)?.placeHolderText;
            return (
              <td>
                <div className="condition">
                  <PurpleTooltip
                    overlay={t(
                      BUSINESS_LOGIC_TAB.CONDITION_SELECT_TOOLTIP_TEXT.key,
                      BUSINESS_LOGIC_TAB.CONDITION_SELECT_TOOLTIP_TEXT.value,
                    )}
                    placement="top"
                    {...variableHintProps}
                  >
                    {!isNew ? (
                      <StyledSelect
                        value={name ? { key: variableId, label: name } : undefined}
                        id={`${variableId}-rule-condition-select`}
                        showSearch={true}
                        showArrow={false}
                        dropdownMatchSelectWidth={false}
                        placeholder={t(
                          BUSINESS_LOGIC_TAB.BL_VARIABLE_PLACEHOLDER.key,
                          BUSINESS_LOGIC_TAB.BL_VARIABLE_PLACEHOLDER.value,
                        )}
                        labelInValue
                        style={{ minWidth: !name ? '150px' : undefined }}
                        onChange={value => handleVariableSelect(value, rowItem, bodyIndex)}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {globalVariables.map(gVariable => (
                          <Option value={gVariable.id}>{gVariable.placeHolderText}</Option>
                        ))}
                      </StyledSelect>
                    ) : (
                      <Placeholder
                        onClick={() => variablePlaceholderClick(false, rowItem, 'isNew')}
                      >
                        {isNewFlow && !flowHints.variableHintDone ? (
                          t(
                            BUSINESS_LOGIC_TAB.BL_VARIABLE_TEXT.key,
                            BUSINESS_LOGIC_TAB.BL_VARIABLE_TEXT.value,
                          )
                        ) : (
                          <Icon type="plus" />
                        )}
                      </Placeholder>
                    )}
                  </PurpleTooltip>
                </div>
              </td>
            );
          }

          const { isNew, operator, value, extraConditions } = rowItem;
          if (isNew) {
            if (isNewFlow && !flowHints.variableHintDone) {
              return (
                <td
                  onMouseEnter={() => setRuleDeleteIndex(rowIndex)}
                  onMouseLeave={() => setRuleDeleteIndex(0)}
                ></td>
              );
            }
          }

          return (
            <td
              onMouseEnter={() => setRuleDeleteIndex(rowIndex)}
              onMouseLeave={() => setRuleDeleteIndex(0)}
            >
              <TableOperator>
                <Select
                  value={operator}
                  onChange={value => updateOperatorByKey(value, rowItem, 'operator')}
                  placeholder={t(
                    BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.key,
                    BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.value,
                  )}
                  dropdownMatchSelectWidth={false}
                  suffixIcon={<Icon type="caret-down" />}
                >
                  {Object.keys(OPERATOR_VALUES).map(key => (
                    <Option value={OPERATOR_VALUES[key]}>{OPERATOR_VALUES[key]}</Option>
                  ))}
                </Select>
                {operator !== OPERATOR_VALUES.EQUALS_ANY_OF ? (
                  <Input
                    value={value}
                    onChange={event => updateOperatorByKey(event.target.value, rowItem, 'value')}
                    placeholder={t(
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                    )}
                  />
                ) : (
                  <input
                    id="tag-input"
                    onKeyPress={event => handleEnterOrBlur(event, rowItem)}
                    onBlur={event => handleEnterOrBlur(event, rowItem)}
                    placeholder={t(
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                    )}
                  />
                )}
              </TableOperator>
              {operator === OPERATOR_VALUES.EQUALS_ANY_OF ? (
                <TableTags rowItem={rowItem} updateOperatorByKey={updateOperatorByKey} />
              ) : (
                <></>
              )}
              {extraConditions.map(extraItem => {
                const { operator: extraOperator, value: extraValue } = extraItem;
                return (
                  <>
                    <TableLogicalOperator>---or---</TableLogicalOperator>
                    <TableOperator>
                      <Select
                        value={extraOperator}
                        onChange={value =>
                          updateOperatorByKey(value, rowItem, 'operator', extraItem)
                        }
                        placeholder={t(
                          BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.key,
                          BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.value,
                        )}
                        dropdownMatchSelectWidth={false}
                        suffixIcon={<Icon type="caret-down" />}
                      >
                        {Object.keys(OPERATOR_VALUES).map(key => (
                          <Option value={OPERATOR_VALUES[key]}>{OPERATOR_VALUES[key]}</Option>
                        ))}
                      </Select>
                      {extraOperator !== OPERATOR_VALUES.EQUALS_ANY_OF ? (
                        <Input
                          value={extraValue}
                          onChange={event =>
                            updateOperatorByKey(event.target.value, rowItem, 'value', extraItem)
                          }
                          placeholder={t(
                            BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                            BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                          )}
                        />
                      ) : (
                        <input
                          id="tag-input"
                          onKeyPress={event => handleEnterOrBlur(event, rowItem, extraItem)}
                          onBlur={event => handleEnterOrBlur(event, rowItem, extraItem)}
                          placeholder={t(
                            BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                            BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                          )}
                        />
                      )}
                      <Popconfirm
                        title={t(
                          BUSINESS_LOGIC_TAB.DELETE_BL_CONFIRM.key,
                          BUSINESS_LOGIC_TAB.DELETE_BL_CONFIRM.value,
                        )}
                        okText={t(
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_YES.key,
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_YES.value,
                        )}
                        cancelText={t(
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_NO.key,
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_NO.value,
                        )}
                        onConfirm={() => deleteExtraCondition(rowItem, extraItem)}
                      >
                        <Tooltip
                          title={t(
                            BUSINESS_LOGIC_TAB.DELETE_CONDITION.key,
                            BUSINESS_LOGIC_TAB.DELETE_CONDITION.value,
                          )}
                        >
                          <Icon type="delete" style={{ alignSelf: 'center' }} />
                        </Tooltip>
                      </Popconfirm>
                    </TableOperator>
                    {extraOperator === OPERATOR_VALUES.EQUALS_ANY_OF ? (
                      <TableTags
                        rowItem={rowItem}
                        extraItem={extraItem}
                        updateOperatorByKey={updateOperatorByKey}
                      />
                    ) : (
                      <></>
                    )}
                  </>
                );
              })}
              {disabled && <div>
                <span
                  onClick={() => addExtraCondition(rowItem)}
                  style={{ fontSize: '12px', cursor: 'pointer' }}
                >
                  <Icon type="plus" />{' '}
                  {t(BUSINESS_LOGIC_TAB.ADD_MORE.key, BUSINESS_LOGIC_TAB.ADD_MORE.value)}
                </span>
              </div>}
            </td>
          );
        })}
        {tableBodyData.length > 1 ? (
          <PurpleTooltip
            title={t(
              BUSINESS_LOGIC_TAB.REMOVE_CONDITION_TOOLTIP.key,
              BUSINESS_LOGIC_TAB.REMOVE_CONDITION_TOOLTIP.value,
            )}
          >
            <div>
              <Icon
                onClick={() => deleteCondition(bodyIndex, tableBodyData[0][1])}
                component={minusIcon}
              />
            </div>
          </PurpleTooltip>
        ) : (
          <></>
        )}
      </tr>
    );
  });
};

export const BusinessLogicModal = ({ modalInfo, onCancel }) => {
  const { t } = useTranslation(NAMESPACES);
  const { businessLogics, setBusinessLogics } = useContext(BusinessLogic);
  const idSelector = useCallback(
    store => ({
      templateInserts: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS],
    }),
    [],
  );

  const isEmailSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE], []);
  const [isEmail] = useDocumentContext(isEmailSelector, 'ContextMenu');

  const contextSelector = useCallback(store => store, []);
  const [documentContext] = useDocumentContext(contextSelector);
  const documentId = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID];


  const groupIdSelector = useCallback(store => store[CONTEXT_KEYS.GROUPED_ELEMENT_IDS], []);
  const [groupedElementIds, updateArtboard] = useArtboardContext(groupIdSelector);


  const blockIdSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK_ID], []);
  const [blockId] = useArtboardContext(blockIdSelector);

  const typeSelector = useCallback(store => store[CONTEXT_KEYS.BUSINESS_LOGIC_MODAL], []);
  const [type] = useArtboardContext(typeSelector);

  const flowID = documentContext.backendIds[modalInfo?.flowUUID];

  const blockDetails = documentContext[blockId]


  const [{ templateInserts }, methods] = useDocumentContext(idSelector);
  const isInsertsLogic = modalInfo?.logicType === 'inserts'
  const isElementLogic = modalInfo?.logicType === 'element'
  const isFlowLogic = modalInfo?.logicType === 'flow'
  const groupNameProp = modalInfo?.logicType === 'element' ? modalInfo.groupName : ''
  const insert = modalInfo?.insert
  const { businessLogic = newBusinessLogic() } = insert || {};
  const { variables = [], id, ...rule } = businessLogic;
  const [saveStatus, setSaveStatus] = useState('');
  const [variablesList, setVariablesList] = useState(deepCopy(variables));
  const [rulesList, setRulesList] = useState(deepCopy([rule]));
  const [selectedLogic, setSelectedLogic] = useState(null);
  const [groupName, setGroupName1] = useState(groupNameProp);
  const [disableTable, setDisableTable] = useState(false);

  const artboardSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );

  const filteredBusinessLogic = getBusinessLogicByType(businessLogics, modalInfo?.logicType);
  const LogicSearchList = getBusinessLogicByType(businessLogics, modalInfo?.logicType, true);

  const [globalVariables, store] = useDocumentContext(artboardSelector);
  const gVariablesWithoutList = useMemo(
    () => globalVariables.filter(gVariable => !EXCLUDED_VARIABLE_TYPES.includes(gVariable.type)),
    [globalVariables],
  );
  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);
  const saveButtonRef = useRef(null);

  const firstCondition = rulesList[0]?.conditions?.[0] || {};
  const hasCondition = !!firstCondition.operator && !!firstCondition.value;
  const saveEnabled = isInsertsLogic
    ? hasCondition
    : isElementLogic ? selectedLogic?.name && groupName && hasCondition : isFlowLogic && selectedLogic && hasCondition;
  const disabled = saveStatus === 'saving';
  const editTableContainerClass = `table-container`;
  const isEditMode = true;
  const isNewFlow = false;
  const tableHeadData = [
    t(BUSINESS_LOGIC_TAB.CONDITIONS_LABEL.key, BUSINESS_LOGIC_TAB.CONDITIONS_LABEL.value),
    t(BUSINESS_LOGIC_TAB.RULE_LABEL.key, BUSINESS_LOGIC_TAB.RULE_LABEL.value),
  ];
  const tableBodyData = variablesList.map((variable, index) => [
    variable,
    ...rulesList.map(rule => ({
      ...rule.conditions[index],
      ruleId: rule.id,
    })),
  ]);

  const validateVariables = variables => {
    let isError = false;
    for (let i = 0; i < variables.length; i++) {
      if (uuidValidate(variables[i].variableId)) {
        message.error(
          t(
            BUSINESS_LOGIC_TAB.EMPTY_CONDITION_ERROR_MESSAGE.key,
            BUSINESS_LOGIC_TAB.EMPTY_CONDITION_ERROR_MESSAGE.value,
            { value: i + 1 },
          ),
        );
        isError = true;
        break;
      }
    }
    return isError;
  };

  const generateExpressions = (conditions, masterVariableId, prevConValid = false) => {
    let expression = '';
    let promptExpression = '';
    conditions.forEach((condition, index) => {
      const { extraConditions, variableId, operator, value } = condition;
      const isExtraCondition = !!(extraConditions && extraConditions.length);
      const varId = masterVariableId || variableId;
      if (value) {
        if (index !== 0 && !masterVariableId && prevConValid) {
          expression += ' && ';
          promptExpression += ' && ';
        }
        if (isExtraCondition) {
          expression += '(';
          promptExpression += '(';
        }
        if (masterVariableId) {
          if (!prevConValid) {
            expression += ' && (';
            promptExpression += ' && (';
          } else {
            expression += ' || ';
            promptExpression += ' || ';
          }
        }
        const variable = globalVariables?.find(variable => variable.id === varId);
        const variableId = variable?.id;
        const variableName = variable?.placeHolderText;
        if (EQUALS_OPERATORS.includes(operator)) {
          expression += `${variableId} ${OPERATOR_SYMBOLS[operator]} '${value}'`;
          promptExpression += `${variableName} ${OPERATOR_SYMBOLS[operator]} '${value}'`;
        } else if (operator === OPERATOR_VALUES.EQUALS_ANY_OF) {
          const result = value
            .split(',')
            .map(v => `'${v}'`)
            .join(',');
          expression += `${variableId} ${OPERATOR_SYMBOLS[operator]} [${result}]`;
          promptExpression += `${variableName} ${OPERATOR_SYMBOLS[operator]} [${result}]`;
        } else if(STARTS_OR_CONTAINS_OPERATOR.includes(operator)) { 
          if(operator === 'Does not contain'){
            expression += `!${variableId}.${OPERATOR_KEY[operator]}('${value}')`;
            promptExpression += `${variableName} Does not contain '${value}'`;
          } else {
            expression += `${variableId}.${OPERATOR_KEY[operator]}('${value}')`;
            promptExpression += `${variableName} ${OPERATOR_KEY[operator]} '${value}'`;
          }
        } else {
          expression += `${variableId} ${OPERATOR_SYMBOLS[operator]} ${value}`;
          promptExpression += `${variableName} ${OPERATOR_SYMBOLS[operator]} ${value}`;
        }
        prevConValid = true;
      } else {
        prevConValid = false;
      }

      if (isExtraCondition) {
        const [extraExp, extPExp] = generateExpressions(extraConditions, variableId, prevConValid);
        if (extraExp) {
          expression += `${extraExp})`;
        }
        if (extPExp) {
          promptExpression += `${extPExp})`;
        }
      }
    });
    return [expression, promptExpression];
  };

  const processVariables = variables => {
    return variables.map(variable => {
      delete variable.isNew;
      return variable;
    });
  };

  const processConditions = conditions => {
    return conditions.map(condition => {
      delete condition.isNew;
      return condition;
    });
  };

  const generateTextFromExpression = async prompt => {
    const response = await generateTextFromPrompt(prompt);
    if (response.statusCode === 2201) {
      let str = response.data.choices[0].text;
      str = new DOMParser().parseFromString(str, 'text/html').documentElement.textContent;
      let dataString = str.split('\n');
      dataString = dataString.join('');
      return dataString;
    }
    return '';
  };


  const processRules = async (rules, groupId) => {
    const promises = [];
    let orderNumber = 1;

    const isAiWritingEnabled =
      ENTERPRISE_ENV && organisationDetails ? organisationDetails.aiWriting : true;
    rules = rules.map(rule => {
      // remove isNew flag before sending to API.
      delete rule.isNew;
      rule.conditions = processConditions(rule.conditions);

      // increment orderNumber
      rule.orderNumber = orderNumber;
      if (isElementLogic || isFlowLogic) {
        if (isElementLogic) {
          if (!rule.groupIDs?.includes(groupId)) {
            rule.groupIDs = rule.groupIDs ? [...rule.groupIDs, groupId] : [groupId];
          }
        }
        rule.flowId = flowID;
        rule.isDeleted = false;
        rule.isStandalone = false;
      }
      orderNumber += 1;

      //generate expression
      const [expression, promptExpression] = generateExpressions(rule.conditions);
      rule.expression = expression;

      if (promptExpression) {
        const prompt = promptExpression
          .replace(/\|\|/g, 'OR')
          .replace(/ && /g, ', ')
          .replace(/\(/g, '')
          .replace(/\)/g, '')
          .replace(/==/g, '=')
          .replace(/ in /g, ' = ')
          .replace(/'/g, '');

        if (isAiWritingEnabled) {
          const query = isElementLogic && groupId ? `Write only the natural language 'if' clause for this condition: ${prompt}, starting with "When:", and end it with exactly 'then show: '. Do not add anything else.` : isInsertsLogic ? `Interpret a meaningful message and translate the following rules into a statement 
          in natural language starting with "When: ${prompt} Then: show ${insert.name} "` : isFlowLogic && `Interpret a meaningful message and translate the following rules into a statement 
          in natural language starting with "When: ${prompt} Then: show page "`;
          promises.push(generateTextFromExpression(query));
        } else {
          promises.push(Promise.resolve(''));
        }
      } else {
        promises.push(Promise.resolve(''));
      }

      return rule;
    });

    const generatedTextList = await Promise.allSettled(promises);
    rules = rules.map((rule, index) => {
      rule.ruleText = generatedTextList[index].value;
      return rule;
    });

    return rules;
  };

  const newBusinessLogicforElement = (name, documentId) => {
    return {
      name: name,
      id: uuid(),
      documentId,
      predicateTruth: "ALL",
      rules: [getDefaultRule1()],
      variables: [getDefaultVariable1()],
    };
  };

  const onSave = async () => {
    //validate before save
    if (validateVariables(variablesList)) {
      return;
    }
    setSaveStatus('saving');
    const variables = processVariables(variablesList);
    const rules = await processRules(rulesList);
    const newBusinessLogic = {
      ...rules[0],
      variables: variables,
      id: uuidValidate(id) ? null : id,
    };
    try {
      const payload = {
        relationId: insert.relationId,
        businessLogic: newBusinessLogic,
      };
      const response = await updateInsertBusinessLogic(payload);
      if (response?.statusCode === 132) {
        setSaveStatus('saved');
        const tempInserts = deepCopy(templateInserts).map(tInsert => {
          if (tInsert.relationId === insert.relationId) {
            tInsert.businessLogic = {
              ...newBusinessLogic,
              id: newBusinessLogic.id || response?.data,
            };
          }
          return tInsert;
        });
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: tempInserts });
        const timeout = setTimeout(() => {
          setSaveStatus('');
          clearTimeout(timeout);
        }, 2000);
      } else {
        setSaveStatus('');
      }
    } catch (e) {
      message.error(
        t(BUSINESS_LOGIC_TAB.SAVE_BL_ERROR.key, BUSINESS_LOGIC_TAB.SAVE_BL_ERROR.value),
      );
      setSaveStatus('');
    }
  };

  const getGroupID = () => {
    const blockDetails = documentContext[blockId];
    let groupId;
    blockDetails.contents.forEach(element => {
      if (element.uuid === groupedElementIds[0]) {
        groupId = element.groupId ? element.groupId : '';
      }
    });
    return groupId;
  }
  const onSave1 = async () => {
    if (isElementLogic) {
      modalInfo.onGroupSave('Group',groupName);
    }
    const blockID = documentContext.backendIds[blockId];
    const groupId = isElementLogic ? getGroupID() : null;

    if (validateVariables(variablesList)) {
      return;
    }
    setSaveStatus('saving');
    const variables = processVariables(variablesList);
    const rules = await processRules(rulesList, groupId, groupName);
    const businessLogic = newBusinessLogicforElement(selectedLogic.name, documentId)
    let BusinessLogic;
    if (isElementLogic) {
      BusinessLogic = {
        ...businessLogic,
        name: selectedLogic.name,
        rules: [rules[0]],
        id: selectedLogic.id ? selectedLogic.id : uuidValidate(id) ? null : id,
        type: type.logicType,
        variables: variables,
        predicateTruth: "FIRST",
        documentId,
      };
    }
    if (isFlowLogic) {
      BusinessLogic = {
        ...businessLogic,
        name: selectedLogic,
        rules: [rules[0]],
        id: modalInfo.businessLogic ? modalInfo.businessLogic.id : null,
        type: 'flow',
        variables: variables,
        predicateTruth: "FIRST",
        documentId,
      };
    }
    const existingGroupMap = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS];
    const existingFlowMap =  store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC];
    try {
      const payload = {
        ...BusinessLogic,
      };
      let updateBusinessLogicFn = updateBusinessLogic;
      if (isEmail) {
        updateBusinessLogicFn = updateEmailBusinessLogic;
      }
      const { statusCode, data } = await updateBusinessLogicFn(payload);
      if (statusCode === 1801 || statusCode === 3101) {
        if (isElementLogic) {
          existingGroupMap.set(groupId, { groupName: groupName, isBLPresent: true });
        }
        if (isFlowLogic) {
          existingFlowMap.set(flowID, { isBLPresent: true, blId: data?.id });
        }
        onCancel();
        message.success(
          t(BUSINESS_LOGIC_TAB.BL_LOGIC_SUCCESS.key, BUSINESS_LOGIC_TAB.BL_LOGIC_SUCCESS.value)
        )
        setBusinessLogics(prev => {
          const foundIndex = prev.findIndex(obj => obj.id === data.id);
          if (foundIndex !== -1) {
            const updated = [...prev];
            updated[foundIndex] = data;
            return updated;
          } else {
            return [...prev, data];
          }
        });
        setSaveStatus('saved');
        const timeout = setTimeout(() => {
          setSaveStatus('');
          clearTimeout(timeout);
        }, 2000);
      }
      else {
        if (isElementLogic) {
          existingGroupMap.set(groupId, { groupName: groupName, isBLPresent: false })
          modalInfo.onGroupSave('UnGroup', groupName);
        }
        setSaveStatus('');
      }
      isElementLogic && store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: existingGroupMap})
      isFlowLogic && store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC]: existingFlowMap})
    } catch (e) {
      if (isElementLogic) {
        existingGroupMap.set(groupId, { groupName: groupName, isBLPresent: false })
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: existingGroupMap })
        modalInfo.onGroupSave('UnGroup', groupName);
      }
      message.error(
        t(BUSINESS_LOGIC_TAB.SAVE_BL_ERROR.key, BUSINESS_LOGIC_TAB.SAVE_BL_ERROR.value),
      );
      setSaveStatus('');
    }
  };
  const addCondition = () => {
    const variable = getDefaultVariable();
    setVariablesList([...variablesList, variable]);
    setRulesList(
      rulesList.map(item => {
        item.conditions = [...item.conditions, getDefaultCondition()];
        return item;
      }),
    );
  };

  const deleteCondition = varIndex => {
    setVariablesList(variablesList.filter((varItem, index) => index !== varIndex));
    setRulesList(
      rulesList.map(rule => {
        rule.conditions = rule.conditions.filter((conItem, index) => {
          return varIndex !== index;
        });
        return rule;
      }),
    );
  };

  const addExtraCondition = operatorItem => {
    setRulesList(
      rulesList.map(rule => {
        if (rule.id === operatorItem.ruleId) {
          rule.conditions = rule.conditions.map(conItem => {
            if (conItem.id === operatorItem.id) {
              conItem.extraConditions = [...conItem.extraConditions, getDefaultExtraCondition()];
            }
            return conItem;
          });
        }
        return rule;
      }),
    );
  };

  const deleteExtraCondition = (operatorItem, extraOperatorItem) => {
    setRulesList(
      rulesList.map(rule => {
        if (rule.id === operatorItem.ruleId) {
          rule.conditions = rule.conditions.map(conItem => {
            if (conItem.id === operatorItem.id) {
              conItem.extraConditions = conItem.extraConditions.filter(
                item => item.id !== extraOperatorItem.id,
              );
            }
            return conItem;
          });
        }
        return rule;
      }),
    );
  };

  const updateVariableWithKey = (value, variableItem, key = 'variableId') => {
    setVariablesList(
      variablesList.map(varItem => {
        if (varItem.variableId === variableItem.variableId) {
          varItem[key] = value;
        }
        return varItem;
      }),
    );
  };

  // TODO: Redo this logic if and when performance gets hit.
  const updateOperatorByKey = (value, operatorItem, key, extraItem = null) => {
    setRulesList(
      rulesList.map(item => {
        if (item.id === operatorItem.ruleId) {
          item.conditions = item.conditions.map(conItem => {
            if (conItem.id === operatorItem.id) {
              if (extraItem) {
                conItem.extraConditions = conItem.extraConditions.map(exItem => {
                  if (exItem.id === extraItem.id) {
                    exItem[key] = value;
                  }
                  return exItem;
                });
              } else {
                conItem[key] = value;
              }
            }
            return conItem;
          });
        }
        return item;
      }),
    );
  };

  const handleSelect = (value) => {
    setSelectedLogic(value);
  };

  const handleGroupNameChange = (e) => {
    const value = e.target.value;
    setGroupName1(value);
  };

  useEffect(() => {
    if (!isInsertsLogic && selectedLogic?.type === 'existing') {
      setDisableTable(true);
      const bl = filteredBusinessLogic.find(b => b.id === selectedLogic?.id);
      setVariablesList(bl?.variables);
      setRulesList(bl?.rules);
    } else if (!isInsertsLogic && selectedLogic?.type === 'new') {      
      setDisableTable(false);
      let newRulesList = deepCopy(rulesList);
      delete newRulesList[0]?.id;
      delete newRulesList[0]?.groupIDs;
      delete newRulesList[0]?.conditions[0].id;
      setVariablesList(variablesList);
      setRulesList(newRulesList);
    }
  }, [selectedLogic])

  useEffect(() => {
    if (modalInfo?.logicType === 'flow' && modalInfo?.businessLogic) {
      const { variables = [], rules = [], name } = modalInfo?.businessLogic;
      setVariablesList(deepCopy(variables));
      setRulesList(deepCopy(rules));
      setSelectedLogic(name);
    }
  }, [modalInfo])


  return (
    <BlModalContainer>
      <Modal
        title={null}
        centered
        visible={true}
        width=""
        onCancel={onCancel}
        style={{ padding: '0px !important' }}
        footer={null}
        className="insertPreviewModalView"
        closable={true}
        maskClosable={false}
        getContainer={document.getElementById('modal-popup-insert-container')}
      >
        <div className='businessLogicModalBody'>
          <div className="add-edit-label">
            {(insert?.businessLogic || modalInfo?.businessLogic)
              ? t(
                BUSINESS_LOGIC_TAB.EDIT_BUSINESS_LOGIC_CAPS_LABEL.key,
                BUSINESS_LOGIC_TAB.EDIT_BUSINESS_LOGIC_CAPS_LABEL.value,
              )
              : t(
                BUSINESS_LOGIC_TAB.ADD_BUSINESS_LOGIC_CAPS_LABEL.key,
                BUSINESS_LOGIC_TAB.ADD_BUSINESS_LOGIC_CAPS_LABEL.value,
              )}
          </div>


          <DisableContainer disabled={disabled} isModal={true}>
            {isElementLogic && <>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <p>
                  {t(
                    BUSINESS_LOGIC_TAB.BUSINESS_LOGIC_NAME.key,
                    BUSINESS_LOGIC_TAB.BUSINESS_LOGIC_NAME.value,
                  )}
                </p>
                <SearchSelect optionsList={LogicSearchList} onSelect={handleSelect} width='75%'
                  placeholder={t(
                    BUSINESS_LOGIC_TAB.BUSINESS_LOGIC_NAME_PLACEHOLDER.key,
                    BUSINESS_LOGIC_TAB.BUSINESS_LOGIC_NAME_PLACEHOLDER.value,
                  )} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <p>
                  {t(
                    BUSINESS_LOGIC_TAB.SECTION_NAME.key,
                    BUSINESS_LOGIC_TAB.SECTION_NAME.value,
                  )}
                </p>
                <div style={{ width: '75%' }}>
                  <Input height={'40px'} className='groupName'
                    placeholder={t(
                      BUSINESS_LOGIC_TAB.SECTION_NAME_PLACEHOLDER1.key,
                      BUSINESS_LOGIC_TAB.SECTION_NAME_PLACEHOLDER1.value,
                    )}
                    value={groupName} onChange={handleGroupNameChange} />
                </div>
              </div>
            </>}

            {isFlowLogic && <>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <p>
                  {t(
                    BUSINESS_LOGIC_TAB.BUSINESS_LOGIC_NAME.key,
                    BUSINESS_LOGIC_TAB.BUSINESS_LOGIC_NAME.value,
                  )}
                </p>
                <div style={{ width: '75%' }}>
                <Input height={'40px'} className='groupName'
                  placeholder={t(
                    BUSINESS_LOGIC_TAB.SECTION_NAME_PLACEHOLDER.key,
                    BUSINESS_LOGIC_TAB.SECTION_NAME_PLACEHOLDER.value,
                  )}
                  value={selectedLogic} onChange={(e) => setSelectedLogic(e.target.value)} />
                  </div>
              </div>
            </>}

            {isInsertsLogic &&
              <BusinessLogicSaveButton1 show={saveEnabled && isEditMode} ref={saveButtonRef}>
                {saveStatus === 'saved' ? (
                  <span>
                    {t(
                      BUSINESS_LOGIC_TAB.SUCCESSFULLY_SAVED.key,
                      BUSINESS_LOGIC_TAB.SUCCESSFULLY_SAVED.value,
                    )}
                  </span>
                ) : (
                  <>
                    {saveStatus !== 'saving' ? (
                      <div onClick={onSave}>
                        {t(
                          BUSINESS_LOGIC_TAB.SAVE_THIS_LOGIC.key,
                          BUSINESS_LOGIC_TAB.SAVE_THIS_LOGIC.value,
                        )}
                      </div>
                    ) : (
                      <Spin indicator={<Icon type="loading" />}></Spin>
                    )}
                  </>
                )}
              </BusinessLogicSaveButton1>
            }
            <DisableContainer disabled={disableTable} isModal={true}>
              <BusinessLogicContainer1 disabled={disabled || disableTable} show={true} showEdit={isEditMode}>
                <RuleEditingContainer1>
                  <TableEditContainer1 className={editTableContainerClass} show={isEditMode}>
                    <table>
                      <TableHead tableHeadData={tableHeadData} t={t} />
                      <tbody>
                        <TableMainBody
                          tableBodyData={tableBodyData}
                          variablesList={variablesList}
                          updateVariableWithKey={updateVariableWithKey}
                          updateOperatorByKey={updateOperatorByKey}
                          addExtraCondition={addExtraCondition}
                          deleteExtraCondition={deleteExtraCondition}
                          deleteCondition={deleteCondition}
                          setRuleDeleteIndex={dummyCallback}
                          globalVariables={gVariablesWithoutList}
                          t={t}
                          isNewFlow={isNewFlow}
                          flowHints={{}}
                          updateFlowHints={dummyCallback}
                           disabled={!disableTable}
                        />

                      </tbody>
                    </table>
                  </TableEditContainer1>
                  {isEditMode && (!disableTable) ? (
                    <BusinessLogicFooter>
                      <div onClick={addCondition} className="new-condition">
                        <Icon component={AddIcon} />{' '}
                        <span>
                          {t(
                            BUSINESS_LOGIC_TAB.NEW_CONDITION.key,
                            BUSINESS_LOGIC_TAB.NEW_CONDITION.value,
                          )}
                        </span>
                      </div>
                    </BusinessLogicFooter>
                  ) : (
                    <></>
                  )}
                </RuleEditingContainer1>
              </BusinessLogicContainer1>
            </DisableContainer>
            {
              (isElementLogic || isFlowLogic) &&
              <BLSaveButton1 show={saveEnabled && isEditMode} ref={saveButtonRef}>
                {saveStatus === 'saved' ? (
                  <span>
                    {t(
                      BUSINESS_LOGIC_TAB.SUCCESSFULLY_SAVED.key,
                      BUSINESS_LOGIC_TAB.SUCCESSFULLY_SAVED.value,
                    )}
                  </span>
                ) : (
                  <>
                    {saveStatus !== 'saving' ? (
                      <div onClick={onSave1}>
                            {selectedLogic?.type === 'existing' && isElementLogic
                              ? t(
                                BUSINESS_LOGIC_TAB.APPLY_LOGIC.key,
                                BUSINESS_LOGIC_TAB.APPLY_LOGIC.value,
                              )
                              : t(
                                BUSINESS_LOGIC_TAB.SAVE_THIS_LOGIC.key,
                                BUSINESS_LOGIC_TAB.SAVE_THIS_LOGIC.value,
                              )
                            }
                      </div>
                    ) : (
                      <Spin indicator={<Icon type="loading" />}></Spin>
                    )}
                  </>
                )}
              </BLSaveButton1>
            }

          </DisableContainer>
        </div>
      </Modal>
    </BlModalContainer>
  );
};
